import { isNil, pickBy } from "lodash";
import { getCheckboxBase } from "../../anchors";
import { ZONE_TYPES, zonesOf } from "../../field-status";
import { meaningfulMappings } from "./draft-content";
import { zoneMappingKeys, zoneName } from "./zones";

const RENAME_THRESHOLD = 0.6;

const distance = (left, right) => {
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (const [row, leftCharacter] of [...left].entries()) {
    const current = [row + 1];
    for (const [column, rightCharacter] of [...right].entries()) {
      current.push(
        Math.min(
          previous[column + 1] + 1,
          current[column] + 1,
          previous[column] + (leftCharacter === rightCharacter ? 0 : 1),
        ),
      );
    }
    previous = current;
  }
  return previous[right.length];
};

const similarity = (left, right) => {
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  const longest = Math.max(a.length, b.length);
  return longest === 0 ? 1 : 1 - distance(a, b) / longest;
};

const hasSettings = (zone, mappings) =>
  zoneMappingKeys(zone).some((key) => !isNil(mappings[key]));

const checkboxSuffix = (anchor) =>
  anchor.slice(getCheckboxBase(anchor).length + 1, -1);

const checkboxBaseOf = (zone) => zone.id.slice(1, -1);

export const planReplacement = ({ anchors, anchorMappings }, nextAnchors) => {
  const mappings = meaningfulMappings(anchorMappings);
  const before = zonesOf(anchors);
  const after = zonesOf(nextAnchors);
  const beforeIds = new Set(before.map((zone) => zone.id));
  const afterIds = new Set(after.map((zone) => zone.id));
  const added = after.filter((zone) => !beforeIds.has(zone.id));
  const lost = before.filter(
    (zone) => !afterIds.has(zone.id) && hasSettings(zone, mappings),
  );
  const pairs = lost
    .flatMap((zone) =>
      added
        .filter((candidate) => candidate.type === zone.type)
        .map((candidate) => ({
          zone,
          candidate,
          score: similarity(zoneName(zone), zoneName(candidate)),
        })),
    )
    .filter(({ score }) => score >= RENAME_THRESHOLD)
    .sort((left, right) => right.score - left.score);
  const guesses = new Map();
  const taken = new Set();
  for (const { zone, candidate } of pairs) {
    if (guesses.has(zone.id) || taken.has(candidate.id)) continue;
    guesses.set(zone.id, candidate.id);
    taken.add(candidate.id);
  }
  return {
    before,
    after,
    kept: after.filter((zone) => beforeIds.has(zone.id)),
    added,
    lost: lost.map((zone) => ({ zone, guess: guesses.get(zone.id) ?? null })),
  };
};

const copyZone = (from, to, mappings) => {
  if (from.type !== to.type) return {};
  if (to.type !== ZONE_TYPES.CHECKBOX_GROUP)
    return isNil(mappings[from.key]) ? {} : { [to.key]: mappings[from.key] };
  const base = checkboxBaseOf(from);
  return Object.fromEntries(
    to.anchors
      .map((anchor) => [anchor, mappings[`[${base}${checkboxSuffix(anchor)}]`]])
      .filter(([, mapping]) => !isNil(mapping)),
  );
};

export const applyReplacement = ({ content, plan, links, anchors }) => {
  const beforeById = new Map(plan.before.map((zone) => [zone.id, zone]));
  const afterById = new Map(plan.after.map((zone) => [zone.id, zone]));
  const mappings = content.anchorMappings || {};
  const kept = plan.kept.reduce(
    (next, zone) => ({
      ...next,
      ...copyZone(beforeById.get(zone.id), zone, mappings),
    }),
    {},
  );
  const anchorMappings = Object.entries(links).reduce(
    (next, [fromId, toId]) => ({
      ...next,
      ...copyZone(beforeById.get(fromId), afterById.get(toId), mappings),
    }),
    kept,
  );
  return {
    ...content,
    anchors,
    anchorMappings,
    proposals: pickBy(content.proposals, (_, zoneId) => afterById.has(zoneId)),
  };
};
